import React, {useCallback, useEffect, useMemo, useState} from "react";
import {ConfigProvider, Divider, message, Select, Spin, Tabs} from "antd";
import {SquareButton} from "../../components";
import {useRequest} from "../../utils/hooks.jsx";
import moment from "moment-timezone";
import {TableDateSearchProvider} from "../Context/TableDateSearchContext.jsx";
import {SearchByCreatedSwitch, TableDateSearch} from "../../components";
import {BusinessDataSection} from "./BusinessDataSection.jsx";
import "./admin-business-data.css";
import {HttpClient} from "../../http/HttpClient.jsx";
import {useListenToSocket} from "../../components/socket/useListenToSocket.jsx";
import {EventBus} from "../../bus/EventBus.jsx";
import common from "@well-b/well-b-shared";
import {StringBuilder} from "../../AppUtils.js";

const ReportPanel = ({
    selectedReport,
    reportOptions,
    onSelectReport,
    onCalculate,
    searchDate,
    setSearchDate,
    activeTab,
    searchByCreated,
    setSearchByCreated,
    isLoadingReport,
    todayButtonText,
    title
}) => {
    return (
        <div className="business-report-container">
            <span className="title">{title}</span>
            <div className="date-search-container">
                <div className="admin-orders-top-bar-right-side">
                    <TableDateSearchProvider>
                        <TableDateSearch
                            date={searchDate}
                            onDatePicked={setSearchDate}
                            todayButtonText={todayButtonText}
                            disableFuture={activeTab === "order" ? searchByCreated : true}
                            allowClear={true}
                            defaultValue={[moment().startOf("month"), moment().endOf("month")]}
                        />
                    </TableDateSearchProvider>
                    {activeTab === "order" && (
                        <SearchByCreatedSwitch
                            searchByCreated={searchByCreated}
                            onChange={() => setSearchByCreated(prev => !prev)}
                        />
                    )}
                </div>
                <SquareButton className="date-search-btn" onClick={onCalculate} disabled={isLoadingReport}>
                    חשב נתונים
                </SquareButton>
                <Select
                    showSearch
                    value={selectedReport?.reportId}
                    className="reports-list-container"
                    placeholder="בחר תוצאות דוח"
                    onChange={id => {
                        const r = reportOptions?.find(o => o.reportId === id);
                        if (r) onSelectReport(r);
                    }}
                    options={reportOptions?.map(({reportId, reportName, createdAt}) => ({
                        value: reportId,
                        label: new StringBuilder(null, "-")
                            .append(reportName)
                            .append(moment(createdAt).format("DD/MM/YYYY HH:mm:ss"))
                            .toString()
                    }))}
                />
            </div>
            {isLoadingReport ? (
                <Spin />
            ) : (
                <div className="business-data-container">
                    {!!selectedReport?.tooltip && <div className="title">{selectedReport?.tooltip}</div>}
                    <div className="admin-business-data-sections">
                        {selectedReport?.statistics?.map((value, index) => (
                            <div className="admin-business-data-sections-row" key={index}>
                                <BusinessDataSection reportData={value} />
                                <Divider className="divider" type="vertical" />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export const AdminBusinessData = () => {
    const [activeTab, setActiveTab] = useState("order");
    const [searchDate, setSearchDate] = useState([moment().startOf("month"), moment().endOf("month")]);
    const [searchByCreated, setSearchByCreated] = useState(true);
    const [selectedOrderReport, setSelectedOrderReport] = useState({});
    const [selectedCompaniesReport, setSelectedCompaniesReport] = useState({});
    const [isLoadingReport, setIsLoadingReport] = useState(false);
    const [reports, isLoading, error, fetchReportsResults] = useRequest(`/admin/api/report/results`, "get");
    const [innerReportsResults, setInnerReportsResults] = useState([]);

    const ORDER_REPORT_NAME = common.report.ReportsNameToText[common.report.ReportName.ADMIN_BUSINESS_DATA_REPORT];
    const COMPANIES_REPORT_NAME =
        common.report.ReportsNameToText[common.report.ReportName.BUSINESS_DATA_COMPANIES_REPORT];

    const orderReports = useMemo(
        () => (innerReportsResults ?? []).filter(r => r.reportName === ORDER_REPORT_NAME),
        [innerReportsResults]
    );
    const companiesReports = useMemo(
        () => (innerReportsResults ?? []).filter(r => r.reportName === COMPANIES_REPORT_NAME),
        [innerReportsResults]
    );

    useEffect(() => {
        if (reports?.reports) {
            setInnerReportsResults(reports.reports);
        }
    }, [reports]);

    useEffect(() => {
        setSearchDate([moment().startOf("month"), moment().endOf("month")]);
    }, [activeTab]);

    const onCalculateOrderData = useCallback(async () => {
        setIsLoadingReport(true);
        const params = {
            byCreatedAt: searchByCreated
        };
        if (searchDate?.[0] && searchDate?.[1]) {
            params.startDate = searchDate[0].valueOf();
            params.endDate = searchDate[1].valueOf();
        }
        const res = await HttpClient.safePost("/admin/api/report/send", {
            reportName: common.report.ReportName.ADMIN_BUSINESS_DATA_REPORT,
            params
        });
        if (res?.error) {
            message.error(res.error);
            setIsLoadingReport(false);
        }
    }, [searchByCreated, searchDate]);

    const onCalculateCompaniesData = useCallback(async () => {
        setIsLoadingReport(true);
        const params = {};
        if (searchDate?.[0] && searchDate?.[1]) {
            params.startDate = searchDate[0].valueOf();
            params.endDate = searchDate[1].valueOf();
        }
        const res = await HttpClient.safePost("/admin/api/report/send", {
            reportName: common.report.ReportName.BUSINESS_DATA_COMPANIES_REPORT,
            params
        });
        if (res?.error) {
            message.error(res.error);
            setIsLoadingReport(false);
        }
    }, [searchDate]);

    const fetchReport = useCallback(async reportId => {
        const res = await HttpClient.safeGet(`/admin/api/report/results/${reportId}`);
        if (res?.error) {
            message.error(res.error);
            return null;
        }
        setInnerReportsResults(prev => [...prev, res]);
        return res;
    }, []);

    const onReceiveReportData = useCallback(
        async message => {
            if (message.status === "success") {
                const report = await fetchReport(message?.reportId);
                fetchReportsResults();
                if (report) {
                    if (report.reportName === ORDER_REPORT_NAME) {
                        setSelectedOrderReport(report);
                    } else if (report.reportName === COMPANIES_REPORT_NAME) {
                        setSelectedCompaniesReport(report);
                    }
                }
            } else if (message.status === "error") {
                EventBus.triggerError("server-error", {
                    content: {
                        description: [
                            `Unfortunately we didn't manage to send your offer :(`,
                            `Error: ${message.errorMessage}`
                        ]
                    }
                });
            }
            setIsLoadingReport(false);
        },
        [fetchReport, fetchReportsResults]
    );

    useListenToSocket({eventName: "RECEIVE_NEW_REPORT_RESULT", onUpdate: onReceiveReportData});

    return (
        <div className="admin-business-data-container">
            <ConfigProvider direction="rtl">
                <div className="tab-title">פילוח נתונים עסקיים</div>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: "order",
                            label: "נתונים עסקיים",
                            children: (
                                <ReportPanel
                                    selectedReport={selectedOrderReport}
                                    reportOptions={orderReports}
                                    onSelectReport={setSelectedOrderReport}
                                    onCalculate={onCalculateOrderData}
                                    searchDate={searchDate}
                                    setSearchDate={setSearchDate}
                                    activeTab={activeTab}
                                    searchByCreated={searchByCreated}
                                    setSearchByCreated={setSearchByCreated}
                                    isLoadingReport={isLoadingReport}
                                    todayButtonText="העסקאות של היום"
                                    title="בחרו את טווח התאריכים להצגת הנתונים הרצויים."
                                />
                            )
                        },
                        {
                            key: "companies",
                            label: "חברות",
                            children: (
                                <ReportPanel
                                    selectedReport={selectedCompaniesReport}
                                    reportOptions={companiesReports}
                                    onSelectReport={setSelectedCompaniesReport}
                                    onCalculate={onCalculateCompaniesData}
                                    searchDate={searchDate}
                                    setSearchDate={setSearchDate}
                                    activeTab={activeTab}
                                    searchByCreated={searchByCreated}
                                    setSearchByCreated={setSearchByCreated}
                                    isLoadingReport={isLoadingReport}
                                    todayButtonText="חברות שהצטרפו היום"
                                    title="כאן תוכלו לצפות בפילוח החברות שנרשמו בטווח התאריכים המבוקש. לא בחרתם תאריכים? נציג לכם את כל נתוני הרישום מתחילת הפעילות."
                                />
                            )
                        }
                    ]}
                />

                {activeTab === "order" && (
                    <div className="pay-attention-container">
                        <span className="pay-attention-title">בעת צפייה בנתונים יש לשים לב</span>
                        <div className="pay-attention-options-container">
                            <span className="pay-attention-option">1. ניתן לשלוף עד שנה אחורה הנתנים </span>
                            <span className="pay-attention-option">2. החל משנת 2025 העמלה שונתה ל 18%</span>
                        </div>
                    </div>
                )}
            </ConfigProvider>
        </div>
    );
};
